import React, { useEffect, useRef, useState, useCallback } from 'react';

/* ═══════════════════════════════
   CANVAS — Particle + Grid field
═══════════════════════════════ */
function HeroCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv.getContext('2d');
    const TAU = Math.PI * 2;
    const rand = (a, b) => Math.random() * (b - a) + a;
    let W = cv.width = window.innerWidth;
    let H = cv.height = window.innerHeight;
    let mouse = { x: W / 2, y: H / 2 };
    const N = 90;

    const pts = Array.from({ length: N }, () => ({
      x: rand(0, W), y: rand(0, H),
      vx: rand(-0.18, 0.18), vy: rand(-0.18, 0.18),
      r: rand(0.8, 2.0),
      life: rand(300, 600), maxLife: 1,
      gold: Math.random() > 0.55,
    }));
    pts.forEach(p => { p.maxLife = p.life; });

    const onMove = e => { mouse.x = e.clientX; mouse.y = e.clientY; };
    const onResize = () => { W = cv.width = window.innerWidth; H = cv.height = window.innerHeight; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('resize', onResize);

    let raf;
    const tick = () => {
      ctx.clearRect(0, 0, W, H);

      /* subtle grid */
      ctx.strokeStyle = 'rgba(217,119,6,0.03)';
      ctx.lineWidth = 1;
      for (let x = 0; x < W; x += 72) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
      for (let y = 0; y < H; y += 72) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

      pts.forEach(p => {
        const dx = p.x - mouse.x, dy = p.y - mouse.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d < 120) { const f = (120 - d) / 120; p.vx += (dx / d) * f * 0.3; p.vy += (dy / d) * f * 0.3; }
        p.vx *= 0.96; p.vy *= 0.96;
        p.x += p.vx; p.y += p.vy; p.life--;
        if (p.life <= 0 || p.x < -10 || p.x > W + 10 || p.y < -10 || p.y > H + 10) {
          const nl = rand(300, 600);
          Object.assign(p, { x: rand(0, W), y: rand(0, H), vx: rand(-0.18, 0.18), vy: rand(-0.18, 0.18), life: nl, maxLife: nl });
        }
        const a = (p.life / p.maxLife) * 0.7;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, TAU);
        ctx.fillStyle = p.gold ? `rgba(217,119,6,${a})` : `rgba(251,191,36,${a * 0.6})`;
        ctx.fill();
      });

      /* connections */
      for (let i = 0; i < N; i++) for (let j = i + 1; j < N; j++) {
        const a = pts[i], b = pts[j];
        const d = Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
        if (d < 100) {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(217,119,6,${0.06 * (1 - d / 100)})`;
          ctx.lineWidth = 0.6; ctx.stroke();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('mousemove', onMove); window.removeEventListener('resize', onResize); };
  }, []);
  return <canvas ref={ref} aria-hidden="true" role="presentation" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
}

/* ═══════════════════════════════
   FLOATING STAT CARD
═══════════════════════════════ */
function StatCard({ val, label, icon, delay, style, className }) {
  const [vis, setVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVis(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div className={className} style={{
      position: 'absolute', ...style,
      opacity: vis ? 1 : 0,
      transform: vis ? 'translateY(0) scale(1)' : 'translateY(18px) scale(0.92)',
      transition: `opacity 0.9s cubic-bezier(.16,1,.3,1), transform 0.9s cubic-bezier(.16,1,.3,1)`,
      background: 'rgba(10,15,28,0.72)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      border: '1px solid rgba(217,119,6,0.18)',
      borderRadius: 16,
      padding: '14px 20px',
      minWidth: 120,
      boxShadow: '0 8px 40px rgba(0,0,0,0.35), 0 0 0 1px rgba(217,119,6,0.06)',
      fontFamily: "'Inter',sans-serif",
      zIndex: 12,
    }}>
      <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#FBBF24', letterSpacing: '-0.03em', lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.4 }}>{label}</div>
      {/* top shimmer line */}
      <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: 1, background: 'linear-gradient(90deg,transparent,rgba(217,119,6,0.5),transparent)', borderRadius: 1 }} />
    </div>
  );
}

/* ═══════════════════════════════
   TRUST STRIP ITEMS
═══════════════════════════════ */
const TRUST = [
  { icon: '🧾', name: 'GST Portal' },
  { icon: '📘', name: 'Tally Prime' },
  { icon: '🏛️', name: 'Income Tax' },
  { icon: '⚡', name: 'Zoho Books' },
  { icon: '🏢', name: 'MCA / ROC' },
  { icon: '☁️', name: 'Cloud Acc.' },
  { icon: '📡', name: 'TRACES' },
  { icon: '🔐', name: 'TDS Returns' },
];

/* ═══════════════════════════════
   MAIN HERO
═══════════════════════════════ */
export default function ResponsiveHeroBanner({
  badgeText           = 'Trusted by 50+ Businesses Across India',
  title               = "Most Businesses Don't Fail —",
  titleLine2          = 'They Break Without Structure',
  description         = 'We design financial systems that bring clarity, control, and scalable growth to your business. Expert Tax, GST, ROC compliance & business consulting.',
  primaryButtonText   = 'Get Free Growth Audit',
  primaryButtonHref   = '/contact',
  secondaryButtonText = 'View Our Approach',
  secondaryButtonHref = '/services',
}) {
  const [on, setOn] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => { const t = setTimeout(() => setOn(true), 100); return () => clearTimeout(t); }, []);
  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const F = (delay) => ({
    opacity: on ? 1 : 0,
    transform: on ? 'translateY(0)' : 'translateY(24px)',
    transition: `opacity 0.9s cubic-bezier(.16,1,.3,1) ${delay}s, transform 0.9s cubic-bezier(.16,1,.3,1) ${delay}s`,
  });

  /* parallax offset */
  const py = scrollY * 0.22;

  return (
    <section style={{
      position: 'relative', minHeight: '100vh', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(160deg,#070D1A 0%,#060B18 45%,#071410 100%)',
      fontFamily: "'Inter',sans-serif",
    }}>
      <style>{`
        @keyframes goldShimmer {
          0%   { background-position: 0% center; }
          100% { background-position: 200% center; }
        }
        @keyframes streakFly {
          0%   { transform: translateX(-200px); opacity: 0; }
          5%   { opacity: 1; }
          90%  { opacity: 0.6; }
          100% { transform: translateX(calc(100vw + 250px)); opacity: 0; }
        }
        @keyframes trustScroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @keyframes orbBreath {
          0%,100% { box-shadow: 0 0 40px rgba(217,119,6,0.3), 0 0 80px rgba(217,119,6,0.1); }
          50%     { box-shadow: 0 0 70px rgba(217,119,6,0.55), 0 0 140px rgba(251,191,36,0.2); }
        }
        @keyframes ringSpinA { from{ transform:rotateX(65deg) rotateZ(0deg); }   to{ transform:rotateX(65deg) rotateZ(360deg); } }
        @keyframes ringSpinB { from{ transform:rotateX(65deg) rotateZ(0deg); }   to{ transform:rotateX(65deg) rotateZ(-360deg); } }
        @keyframes floatA { 0%,100%{ transform:translateY(0) rotate(0deg); } 50%{ transform:translateY(-12px) rotate(1.5deg); } }
        @keyframes floatB { 0%,100%{ transform:translateY(0) rotate(0deg); } 50%{ transform:translateY(-9px) rotate(-1deg); } }
        @keyframes btnShimmer { 0%{ left:-100%; } 100%{ left:200%; } }
        @keyframes lineGrow { from{ transform:scaleX(0) } to{ transform:scaleX(1) } }
        @keyframes glowPulse { 0%,100%{ opacity:0.4; } 50%{ opacity:0.85; } }

        .hero-primary-btn:hover { transform: translateY(-2px); box-shadow: 0 16px 48px rgba(217,119,6,0.45) !important; }
        .hero-secondary-btn:hover { border-color: rgba(217,119,6,0.45) !important; color: #FBBF24 !important; }
        .trust-pill:hover { border-color: rgba(217,119,6,0.4) !important; box-shadow: 0 0 16px rgba(217,119,6,0.15) !important; }

        /* ── Mobile responsive overrides ── */
        .hero-stat-card { display: block; }
        @media (max-width: 767px) {
          .hero-stat-card { display: none !important; }
          .hero-hud-bracket { display: none !important; }
          .hero-orb-ring { display: none !important; }
        }
        @media (max-width: 480px) {
          .hero-cta-wrap { flex-direction: column !important; align-items: stretch !important; }
          .hero-primary-btn, .hero-secondary-btn { justify-content: center !important; width: 100% !important; box-sizing: border-box !important; }
        }
      `}</style>

      {/* ── Canvas field ── */}
      <HeroCanvas />

      {/* ── Radial ambient glows ── */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `
          radial-gradient(ellipse 70% 55% at 65% 35%, rgba(217,119,6,0.07) 0%, transparent 65%),
          radial-gradient(ellipse 50% 40% at 20% 70%, rgba(6,78,59,0.06) 0%, transparent 65%),
          radial-gradient(ellipse 40% 30% at 80% 80%, rgba(217,119,6,0.04) 0%, transparent 60%)
        `,
        transform: `translateY(${-py * 0.3}px)`,
      }} />

      {/* ── Light streaks ── */}
      {[
        { top: '15%', dur: '7s',   delay: '0s',   w: 260, op: 0.45 },
        { top: '38%', dur: '5.5s', delay: '2.2s', w: 180, op: 0.35 },
        { top: '62%', dur: '9s',   delay: '1.0s', w: 320, op: 0.3  },
        { top: '82%', dur: '6.5s', delay: '3.8s', w: 200, op: 0.25 },
      ].map((s, i) => (
        <div key={i} style={{
          position: 'absolute', top: s.top, left: 0, pointerEvents: 'none',
          width: s.w, height: 1,
          background: `linear-gradient(90deg,transparent,rgba(217,119,6,${s.op}),rgba(251,191,36,${s.op * 0.8}),transparent)`,
          animation: `streakFly ${s.dur} linear ${s.delay} infinite`,
        }} />
      ))}

      {/* ── HUD corner brackets ── */}
      {[
        { top: 88, left: 24, borderTop: '1.5px solid', borderLeft: '1.5px solid' },
        { top: 88, right: 24, borderTop: '1.5px solid', borderRight: '1.5px solid' },
        { bottom: 24, left: 24, borderBottom: '1.5px solid', borderLeft: '1.5px solid' },
        { bottom: 24, right: 24, borderBottom: '1.5px solid', borderRight: '1.5px solid' },
      ].map((s, i) => (
        <div key={i} className="hero-hud-bracket" style={{
          ...s, position: 'absolute', width: 32, height: 32,
          borderColor: 'rgba(217,119,6,0.35)', pointerEvents: 'none',
          opacity: on ? 1 : 0, transition: `opacity 1.2s ease ${0.3 + i * 0.1}s`,
        }} />
      ))}

      {/* ── Floating stat cards (hidden on mobile) ── */}
      <StatCard val="50+"  label="Businesses Protected" icon="🛡️" delay={900}  className="hero-stat-card" style={{ top: '22%', right: '6%', animation: 'floatA 7s ease-in-out infinite' }} />
      <StatCard val="10+"  label="Years Expertise"      icon="📈" delay={1100} className="hero-stat-card" style={{ top: '42%', right: '4%', animation: 'floatB 9s ease-in-out infinite 1s' }} />
      <StatCard val="0"    label="Penalties Incurred"   icon="✅" delay={1300} className="hero-stat-card" style={{ top: '62%', right: '7%', animation: 'floatA 8s ease-in-out infinite 2s' }} />

      {/* ═══ MAIN CONTENT ═══ */}
      <div style={{
        position: 'relative', zIndex: 10, flex: 1,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: 'clamp(80px, 12vw, 120px) clamp(16px, 5vw, 48px) 48px',
        textAlign: 'center',
        transform: `translateY(${-py}px)`,
        boxSizing: 'border-box',
        width: '100%',
      }}>

        {/* Badge */}
        <div style={{
          ...F(0.15),
          display: 'inline-flex', alignItems: 'center', gap: 8,
          borderRadius: 40, padding: '5px 16px 5px 6px',
          marginBottom: 24,
          background: 'rgba(217,119,6,0.08)',
          border: '1px solid rgba(217,119,6,0.22)',
          backdropFilter: 'blur(12px)',
        }}>
          <span style={{
            background: 'linear-gradient(135deg,#D97706,#FBBF24)',
            borderRadius: 20, padding: '2px 10px',
            fontSize: 10, fontWeight: 700, color: '#0A0F1C', letterSpacing: '0.06em',
          }}>INDIA</span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', fontWeight: 400 }}>{badgeText}</span>
        </div>

        {/* ── Headline ── */}
        <div style={{ ...F(0.3), maxWidth: 780, marginBottom: 16, width: '100%' }}>
          <h1 style={{
            margin: 0,
            fontWeight: 800,
            fontSize: 'clamp(1.75rem, 6vw, 3.2rem)',
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            color: '#FDFBF7',
          }}>
            {title}
            <br />
            <span style={{
              background: 'linear-gradient(135deg,#FBBF24 0%,#D97706 30%,#F59E0B 60%,#FBBF24 100%)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'goldShimmer 5s linear infinite',
              display: 'inline',
            }}>{titleLine2}</span>
          </h1>
          {/* Animated underline */}
          <div style={{
            margin: '12px auto 0', height: 2, width: 180,
            background: 'linear-gradient(90deg,transparent,#D97706,#FBBF24,transparent)',
            transformOrigin: 'center',
            animation: on ? 'lineGrow 1.2s cubic-bezier(.16,1,.3,1) 1s both' : 'none',
          }} />
        </div>

        {/* Description */}
        <p style={{
          ...F(0.55),
          maxWidth: 560, margin: '0 0 28px',
          fontSize: 'clamp(0.875rem, 2.5vw, 0.98rem)',
          lineHeight: 1.75, color: 'rgba(255,255,255,0.5)', fontWeight: 400,
          padding: '0 4px',
        }}>{description}</p>

        {/* ── CTAs ── */}
        <div className="hero-cta-wrap" style={{ ...F(0.75), display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginBottom: 36, width: '100%', maxWidth: 420, padding: '0 8px', boxSizing: 'border-box' }}>
          {/* Primary gold CTA */}
          <a href={primaryButtonHref} className="hero-primary-btn" style={{
            position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 28px', borderRadius: 40, overflow: 'hidden',
            background: 'linear-gradient(135deg,#D97706,#FBBF24)',
            color: '#0A0F1C', textDecoration: 'none',
            fontWeight: 700, fontSize: 13, letterSpacing: '0.02em',
            boxShadow: '0 8px 32px rgba(217,119,6,0.35)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          }}>
            <span style={{ position: 'relative', zIndex: 1 }}>{primaryButtonText}</span>
            <svg style={{ position: 'relative', zIndex: 1 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0A0F1C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            <div style={{
              position: 'absolute', top: 0, left: '-100%', width: '55%', height: '100%',
              background: 'linear-gradient(105deg,transparent,rgba(255,255,255,0.35),transparent)',
              transform: 'skewX(-20deg)',
              animation: 'btnShimmer 3s ease 1.8s infinite',
            }} />
          </a>

          {/* Secondary ghost CTA */}
          <a href={secondaryButtonHref} className="hero-secondary-btn" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 24px', borderRadius: 40,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(217,119,6,0.2)',
            color: 'rgba(255,255,255,0.72)', textDecoration: 'none',
            fontWeight: 500, fontSize: 13,
            backdropFilter: 'blur(12px)',
            transition: 'border-color 0.3s, color 0.3s',
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
            {secondaryButtonText}
          </a>
        </div>

        {/* ── Holographic orb ── */}
        <div style={{ ...F(0.95), display: 'flex', justifyContent: 'center', marginBottom: 36 }}>
          <div style={{
            position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 64, height: 64, borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 35%,rgba(251,191,36,0.5),rgba(217,119,6,0.8),rgba(5,10,22,0.9))',
            animation: 'orbBreath 4s ease-in-out infinite',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 24, position: 'relative', zIndex: 2 }}>⚖️</span>
            {[{ sz: 100, anim: 'ringSpinA 10s linear infinite', op: 0.2 },
              { sz: 132, anim: 'ringSpinB 14s linear infinite', op: 0.14 },
              { sz: 164, anim: 'ringSpinA 20s linear infinite', op: 0.08 }].map((r, i) => (
              <div key={i} className="hero-orb-ring" style={{
                position: 'absolute', width: r.sz, height: r.sz, borderRadius: '50%',
                border: `1px solid rgba(217,119,6,${r.op})`,
                animation: r.anim, pointerEvents: 'none',
              }} />
            ))}
            {/* glow */}
            <div style={{
              position: 'absolute', inset: -30, borderRadius: '50%',
              background: 'radial-gradient(circle,rgba(217,119,6,0.12),transparent 70%)',
              filter: 'blur(12px)', animation: 'glowPulse 4s ease-in-out infinite',
            }} />
          </div>
        </div>

        {/* ── Trust marquee ── */}
        <div style={{ ...F(1.1), width: '100%', maxWidth: 860 }}>
          <p style={{
            fontSize: '0.6rem', fontWeight: 500, letterSpacing: '0.2em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)',
            margin: '0 0 12px', textAlign: 'center',
          }}>Integrated Platforms &amp; Regulatory Portals</p>
          <div style={{
            overflow: 'hidden',
            maskImage: 'linear-gradient(90deg,transparent 0%,black 10%,black 90%,transparent 100%)',
            WebkitMaskImage: 'linear-gradient(90deg,transparent 0%,black 10%,black 90%,transparent 100%)',
          }}>
            <div
              style={{ display: 'flex', gap: 10, animation: 'trustScroll 28s linear infinite', width: 'max-content' }}
              onMouseEnter={e => e.currentTarget.style.animationPlayState = 'paused'}
              onMouseLeave={e => e.currentTarget.style.animationPlayState = 'running'}
            >
              {[...TRUST, ...TRUST].map((t, i) => (
                <span key={i} className="trust-pill" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '6px 16px', borderRadius: 40,
                  background: 'rgba(217,119,6,0.05)',
                  border: '1px solid rgba(217,119,6,0.12)',
                  backdropFilter: 'blur(8px)',
                  whiteSpace: 'nowrap', flexShrink: 0,
                  fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.6)',
                  cursor: 'default', transition: 'border-color 0.3s, box-shadow 0.3s',
                }}>
                  <span style={{ fontSize: 14 }}>{t.icon}</span>{t.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Scroll hint ── */}
        <div style={{
          ...F(1.3),
          marginTop: 32,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        }}>
          <span style={{ fontSize: 9, letterSpacing: '0.25em', color: 'rgba(217,119,6,0.4)', textTransform: 'uppercase' }}>Scroll</span>
          <div style={{
            width: 1, height: 32,
            background: 'linear-gradient(to bottom, rgba(217,119,6,0.5), transparent)',
            animation: 'glowPulse 2s ease-in-out infinite',
          }} />
        </div>
      </div>

      {/* ── Bottom fade ── */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 90,
        background: 'linear-gradient(transparent,#060B18)',
        pointerEvents: 'none', zIndex: 5,
      }} />
    </section>
  );
}
